import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const AUTH_STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

body {
  font-family: 'Manrope', sans-serif;
  background: #ffffff;
  color: #0f172a;
  -webkit-font-smoothing: antialiased;
}

.auth-shell {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  padding: 24px;
}

.auth-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 40px 40px 32px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.04), 0 10px 40px rgba(0,0,0,0.06);
}

.auth-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 32px;
}

.auth-logo-mark {
  width: 36px; height: 36px;
  background: #059669;
  border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.auth-logo-name {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -.3px;
}

.auth-logo-name span { color: #059669; }

.auth-form-title {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -.4px;
  margin-bottom: 6px;
}

.auth-form-sub {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 28px;
  line-height: 1.5;
}

.auth-field { margin-bottom: 14px; }

.auth-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 5px;
}

.auth-input {
  width: 100%;
  padding: 10px 13px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-family: 'Manrope', sans-serif;
  color: #0f172a;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
}

.auth-input:focus {
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(5,150,105,0.08);
  background: #fff;
}

.auth-input::placeholder { color: #94a3b8; }

.auth-submit {
  width: 100%;
  padding: 11px;
  background: #059669;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Manrope', sans-serif;
  cursor: pointer;
  transition: background .15s;
  margin-top: 6px;
  margin-bottom: 18px;
}

.auth-submit:hover { background: #047857; }
.auth-submit:disabled { opacity: .5; cursor: not-allowed; }

.auth-switch {
  text-align: center;
  font-size: 13px;
  color: #64748b;
}

.auth-switch button {
  background: none;
  border: none;
  color: #059669;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  font-family: 'Manrope', sans-serif;
  padding: 0;
}

.auth-switch button:hover { text-decoration: underline; }

.auth-error {
  background: #fef2f2;
  border: 1px solid rgba(220,38,38,0.2);
  color: #dc2626;
  border-radius: 8px;
  padding: 10px 13px;
  font-size: 12px;
  margin-bottom: 14px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.auth-success {
  background: #ecfdf5;
  border: 1px solid rgba(5,150,105,0.2);
  color: #059669;
  border-radius: 8px;
  padding: 10px 13px;
  font-size: 12px;
  margin-bottom: 14px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.auth-footer {
  text-align: center;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 24px;
}

@media (max-width: 480px) {
  .auth-card { padding: 32px 24px 24px; border-radius: 16px; }
}
`;

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [legalModal, setLegalModal] = useState(null);

  const reset = () => { setError(""); setSuccess(""); };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isRecovery = params.get('type') === 'recovery' ||
                       params.get('type_recovery') === '1' ||
                       window.location.hash.includes('type=recovery') ||
                       localStorage.getItem('recovery_pending') === '1';

    if (isRecovery) {
      localStorage.removeItem('recovery_pending');
      setMode("reset");
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setMode("reset");
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError("La contraseña debe tener al menos 6 caracteres."); return; }
    setLoading(true); reset();

    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      const params = new URLSearchParams(hash.replace('#', ''));
      const token = params.get('access_token');
      if (token) {
        const { error: otpError } = await supabase.auth.verifyOtp({ token_hash: token, type: 'recovery' });
        if (otpError) { setLoading(false); setError(otpError.message); return; }
      }
    }

    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setLoading(false);
    if (error) setError(error.message);
    else { setSuccess("¡Contraseña actualizada correctamente! Ya puedes iniciar sesión."); setTimeout(() => setMode("login"), 2000); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); reset();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) setError(error.message);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError("La contraseña debe tener al menos 6 caracteres."); return; }
    if (!accepted) { setError("Debes aceptar los Términos y la Política de Privacidad para continuar."); return; }
    setLoading(true); reset();
    const { error } = await supabase.auth.signUp({ email, password });
    setLoading(false);
    if (error) setError(error.message);
    else setSuccess("¡Cuenta creada! Revisa tu email para confirmar el registro.");
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true); reset();
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}?type=recovery`,
    });
    setLoading(false);
    if (error) setError(error.message);
    else { localStorage.setItem('recovery_pending', '1'); setSuccess("Te hemos enviado un enlace para restablecer tu contraseña."); }
  };

  return (
    <>
      <style>{AUTH_STYLES}</style>
      <div className="auth-shell">
        <div className="auth-card">

          {/* Logo */}
          <div className="auth-logo">
            <div className="auth-logo-mark">📈</div>
            <div className="auth-logo-name">Trade<span>Leash</span></div>
          </div>

          {mode === "login" && (
            <>
              <div className="auth-form-title">Bienvenido de vuelta</div>
              <div className="auth-form-sub">Inicia sesión para acceder a tu journal y estadísticas.</div>
              {error && <div className="auth-error"><span>❌</span><span>{error}</span></div>}
              <form onSubmit={handleLogin}>
                <div className="auth-field">
                  <label className="auth-label">Email</label>
                  <input className="auth-input" type="email" placeholder="tu@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                <div className="auth-field">
                  <label className="auth-label">Contraseña</label>
                  <input className="auth-input" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required />
                </div>
                <div style={{ textAlign: 'right', marginBottom: 16, marginTop: -6 }}>
                  <button type="button" onClick={() => { setMode("forgot"); reset(); }} style={{ background: 'none', border: 'none', color: '#059669', fontSize: 12, cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}>
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <button className="auth-submit" type="submit" disabled={loading}>
                  {loading ? "Iniciando sesión..." : "Iniciar sesión →"}
                </button>
              </form>
              <div className="auth-switch">
                ¿No tienes cuenta?{" "}
                <button onClick={() => { setMode("register"); reset(); }}>Regístrate gratis</button>
              </div>
            </>
          )}

          {mode === "register" && (
            <>
              <div className="auth-form-title">Crea tu cuenta</div>
              <div className="auth-form-sub">7 días gratis. Sin tarjeta de crédito.</div>
              {error && <div className="auth-error"><span>❌</span><span>{error}</span></div>}
              {success && <div className="auth-success"><span>✅</span><span>{success}</span></div>}
              {!success && (
                <form onSubmit={handleRegister}>
                  <div className="auth-field">
                    <label className="auth-label">Email</label>
                    <input className="auth-input" type="email" placeholder="tu@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
                  </div>
                  <div className="auth-field">
                    <label className="auth-label">Contraseña</label>
                    <input className="auth-input" type="password" placeholder="Mínimo 6 caracteres" value={password} onChange={e => setPassword(e.target.value)} required />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, marginTop: 4 }}>
                    <input
                      type="checkbox"
                      id="accept-terms"
                      checked={accepted}
                      onChange={e => setAccepted(e.target.checked)}
                      style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0, accentColor: '#059669', cursor: 'pointer' }}
                    />
                    <label htmlFor="accept-terms" style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, cursor: 'pointer' }}>
                      He leído y acepto los{' '}
                      <button type="button" onClick={e => { e.preventDefault(); setLegalModal('terms'); }} style={{ color: '#059669', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: 'Manrope, sans-serif' }}>Términos y Condiciones</button>
                      {' '}y la{' '}
                      <button type="button" onClick={e => { e.preventDefault(); setLegalModal('privacy'); }} style={{ color: '#059669', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: 'Manrope, sans-serif' }}>Política de Privacidad</button>
                    </label>
                  </div>
                  <button className="auth-submit" type="submit" disabled={loading || !accepted} style={{ opacity: !accepted ? 0.5 : 1 }}>
                    {loading ? "Creando cuenta..." : "Crear cuenta gratis →"}
                  </button>
                </form>
              )}
              <div className="auth-switch">
                ¿Ya tienes cuenta?{" "}
                <button onClick={() => { setMode("login"); reset(); }}>Inicia sesión</button>
              </div>
            </>
          )}

          {mode === "reset" && (
            <>
              <div className="auth-form-title">Nueva contraseña</div>
              <div className="auth-form-sub">Elige una contraseña segura para tu cuenta.</div>
              {error && <div className="auth-error"><span>❌</span><span>{error}</span></div>}
              {success && <div className="auth-success"><span>✅</span><span>{success}</span></div>}
              {!success && (
                <form onSubmit={handleReset}>
                  <div className="auth-field">
                    <label className="auth-label">Nueva contraseña</label>
                    <input className="auth-input" type="password" placeholder="Mínimo 6 caracteres" value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
                  </div>
                  <button className="auth-submit" type="submit" disabled={loading}>
                    {loading ? "Guardando..." : "Guardar nueva contraseña →"}
                  </button>
                </form>
              )}
            </>
          )}

          {mode === "forgot" && (
            <>
              <div className="auth-form-title">Restablecer contraseña</div>
              <div className="auth-form-sub">Te enviamos un enlace a tu email para crear una nueva contraseña.</div>
              {error && <div className="auth-error"><span>❌</span><span>{error}</span></div>}
              {success && <div className="auth-success"><span>✅</span><span>{success}</span></div>}
              {!success && (
                <form onSubmit={handleForgot}>
                  <div className="auth-field">
                    <label className="auth-label">Email</label>
                    <input className="auth-input" type="email" placeholder="tu@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
                  </div>
                  <button className="auth-submit" type="submit" disabled={loading}>
                    {loading ? "Enviando..." : "Enviar enlace →"}
                  </button>
                </form>
              )}
              <div className="auth-switch">
                <button onClick={() => { setMode("login"); reset(); }}>← Volver al login</button>
              </div>
            </>
          )}

          <div className="auth-footer">© 2026 TradeLeash · Trading Journal</div>

        </div>
      </div>

      {/* Legal Modal */}
      {legalModal && (
        <div onClick={() => setLegalModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 32, width: '100%', maxWidth: 600, maxHeight: '80vh', overflowY: 'auto', position: 'relative' }}>
            <button onClick={() => setLegalModal(null)} style={{ position: 'absolute', top: 14, right: 14, background: '#f1f5f9', border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>

            {legalModal === 'terms' && <>
              <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: '#0f172a' }}>Términos y Condiciones</h2>
              {[
                ['1. Titular', 'TradeLeash es un servicio operado por una persona física con residencia en España. Para consultas, use el correo de soporte de la plataforma.'],
                ['2. Aceptación', 'El acceso y uso de TradeLeash implica la aceptación plena de estos Términos. Si no está de acuerdo, absténgase de usar el Servicio.'],
                ['3. Descripción', 'TradeLeash es una herramienta de journal de trading. No constituye asesoramiento financiero ni de inversión. Toda decisión de trading es responsabilidad exclusiva del usuario.'],
                ['4. Responsabilidad', 'El Titular no será responsable de pérdidas económicas derivadas del uso de la plataforma ni del fallo técnico del sistema de bloqueo automático.'],
                ['5. Uso aceptable', 'Queda prohibido intentar acceder a datos de otros usuarios, realizar ingeniería inversa, utilizar el Servicio para actividades ilegales o revender el Servicio sin autorización.'],
                ['6. Propiedad intelectual', 'Todos los derechos sobre el Servicio pertenecen al Titular. Queda prohibida su reproducción sin autorización expresa.'],
                ['7. Precios y cancelación', 'Los precios se muestran en la página de precios. El usuario puede cancelar en cualquier momento. No se ofrecen reembolsos por periodos ya facturados salvo exigencia legal.'],
                ['8. Legislación', 'Estos Términos se rigen por la legislación española. Última actualización: abril 2025.'],
              ].map(([title, text]) => (
                <div key={title} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{title}</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6 }}>{text}</div>
                </div>
              ))}
            </>}

            {legalModal === 'privacy' && <>
              <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: '#0f172a' }}>Política de Privacidad</h2>
              {[
                ['1. Responsable', 'El responsable del tratamiento es el titular de TradeLeash, persona física con residencia en España.'],
                ['2. Datos que recopilamos', 'Email para autenticación, datos de trading (trades, notas, capturas), configuración de cuentas y datos técnicos de uso (IP, navegador, fecha de acceso).'],
                ['3. Finalidad', 'Prestación del Servicio, comunicaciones relacionadas con el mismo, mejora de la plataforma y cumplimiento de obligaciones legales.'],
                ['4. Base legal', 'Ejecución del contrato (Art. 6.1.b RGPD), consentimiento para comunicaciones (Art. 6.1.a RGPD) e interés legítimo para mejora del Servicio (Art. 6.1.f RGPD).'],
                ['5. Conservación', 'Sus datos se conservan mientras mantenga cuenta activa. Tras cancelarla, se eliminan en máximo 30 días.'],
                ['6. Destinatarios', 'Supabase Inc. (base de datos y autenticación) y Vercel Inc. (alojamiento web). Ambos cumplen el RGPD. No vendemos sus datos a terceros.'],
                ['7. Sus derechos', 'Tiene derecho de acceso, rectificación, supresión, limitación, portabilidad y oposición. Puede ejercerlos por email o reclamar ante la AEPD (www.aepd.es).'],
                ['8. Seguridad', 'Usamos HTTPS/TLS, autenticación segura y Row Level Security en base de datos. Última actualización: abril 2025.'],
              ].map(([title, text]) => (
                <div key={title} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{title}</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6 }}>{text}</div>
                </div>
              ))}
            </>}

            <button onClick={() => setLegalModal(null)} style={{ marginTop: 16, width: '100%', padding: '10px', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}>Entendido</button>
          </div>
        </div>
      )}
    </>
  );
}
